// ** React Imports
import { useEffect, useState } from "react"
// ** Store
import { useSelector } from "react-redux"
// ** Custom Components
import Timeline from "@components/timeline"
import CardAction from "@components/card-actions"
import RenderModal from "../modal-forms/RenderModal"
import EventForm from "../modal-forms/EventForm"
import ComplaitForm from "../modal-forms/ComplaitForm"
import NewOfferSaleForm from "../modal-forms/NewOfferSaleForm"
// ** Icons Feather
import {
  Edit,
  MapPin,
  ShoppingCart,
  Flag,
  FileText,
  MoreHorizontal,
  AlertCircle,
  Eye,
  User,
  Briefcase
} from "react-feather"
// ** Reactstrap
import {
  Card,
  CardBody,
  Spinner,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledButtonDropdown,
  Button,
  CardHeader,
  Alert,
  Input,
  Label
} from "reactstrap"
// ** API calls
import {
  getAllFromController,
  getPersonHistory,
  getLoggedInUser,
  addEventInPersonHistory
} from "@lib/api"
import { eventColorSelection, dateISO, getISODateUTC } from "@lib/util"
import { EVENT_ID_RANGES, VALIDATED_OFFER_ID } from "@lib/constants"
import DeleteConfirmation from "../../components/DeleteConfirmation"
import ValidateConfirmation from "../../components/ValidateConfirmation"
import { deleteEventInPersonHistory } from "../../../../lib/api"

const BasicTimeline = ({ personId, isAdminCRM = false, editMode = true }) => {
  // ** Hooks and States
  const personStore = useSelector((state) => state.persons)
  const [data, setData] = useState()
  const [activeModal, setActiveModal] = useState(null)
  const [eventToEdit, setEventToEdit] = useState(null)
  const [selectedEventType, setSelectedEventType] = useState(null)

  // Displaying modals
  const toggleModal = (id, event, bypass = false) => {
    // special checks for offer/sale
    if (["offer", "sale"].includes(id) && !bypass) {
      // if no email/phone/address, show error
      if (
        personStore.selectedPerson.emails.length === 0 ||
        personStore.selectedPerson.phones.length === 0 ||
        personStore.selectedPerson.addresses.length === 0
      ) {
        setActiveModal("missingData")
        setSelectedEventType(id)
        return
      }
    }
    if (activeModal !== id) {
      setActiveModal(id)
      setEventToEdit(event)
    } else {
      setActiveModal(null)
      setEventToEdit(null)
    }
  }

  // ** Get person history
  const getPersonHistoryHandler = async () => {
    const formatedData = []
    const user = await getLoggedInUser()
    const historyData = await getPersonHistory(personId)
    // forming the data for the timeline
    for (let index = 0; index < historyData.length; index++) {
      const historyItem = historyData[index]
      // manual obj creation to avoid errors
      let event = {
        ...(historyItem.amount && { amount: historyItem.amount }),
        ...(historyItem.currency && {
          currency: {
            value: historyItem.currency.id,
            label: `${historyItem.currency.iso3} ${historyItem.currency.name}`
          }
        }),
        id: historyItem.id,
        description: historyItem.description,
        configOrderId: historyItem.configOrderId,
        createdOn: historyItem.createdOn,
        eventReason: {
          value: historyItem.eventReason.id,
          label: historyItem.eventReason.name
        },
        on: historyItem.on
      }

      const createEventComplaintSuccess = async () => {
        const successComplaint = {
          70: 30,
          71: 31,
          72: 32,
          73: 33,
          74: 34
        }
        const theEventReasonId = successComplaint[event.eventReason.value]
        const theDescription = `This complaint of ${dateISO(
          historyItem.on,
          true,
          true
        )} has been resolved.`
        const cleanData = {
          interactionId: historyItem.interactionId,
          collaboratorId: user.collaboratorId,
          showroomId: user.showroomId,
          description: theDescription,
          eventReasonId: theEventReasonId,
          // weird formatting with flatpickr
          on: getISODateUTC(new Date())
        }
        await deleteEventInPersonHistory(historyItem.id)
        const response = await addEventInPersonHistory(cleanData)
        return response
      }

      // partners infos if existant
      let partnerHtml = null
      if (historyItem.partnerId) {
        const partner = await getAllFromController(`Partners/${historyItem.partnerId}`, "crm")
        event = { ...event, partner: { value: partner.id, label: partner.name } }
        partnerHtml = (
          <div className="d-flex">
            <Briefcase color="#bbbbbb" size={20} />
            <div className="mt-sm-0 m-1">
              <p className="text-muted mb-50">Partner</p>
              <p className="mb-0">{event.partner.label}</p>
            </div>
          </div>
        )
      }

      // Showroom infos
      let showroomHtml = null
      // get showroom infos if needed and add it to 'event' for later uses
      if (historyItem.showroomId) {
        const showroom = await getAllFromController(`Showrooms/${historyItem.showroomId}`, "crm")
        event = { ...event, showroom: { value: showroom.id, label: showroom.name } }
        showroomHtml = (
          <div className="d-flex">
            <MapPin color="#bbbbbb" size={20} />
            <div className="mt-sm-0 m-1">
              <p className="text-muted mb-50">Showroom</p>
              <p className="mb-0">{event.showroom.label}</p>
            </div>
          </div>
        )
      }

      // Collaborator infos (only for display purpose)
      let collaboratorHtml = null
      if (historyItem.collaborator) {
        collaboratorHtml = (
          <div className="d-flex">
            <User color="#bbbbbb" size={20} />
            <div className="mt-sm-0 m-1">
              <p className="text-muted mb-50">Collaborator</p>
              <p className="mb-0">
                {historyItem.collaborator.firstName} {historyItem.collaborator.name}
              </p>
            </div>
          </div>
        )
      }

      // Amount infos
      let amountHtml = null
      if (event.amount) {
        amountHtml = (
          <div className="d-flex">
            <ShoppingCart color="#bbbbbb" size={20} />
            <div className="mt-sm-0 m-1">
              <p className="text-muted mb-50">Amount</p>
              <p className="mb-0">
                {event.amount} {historyItem.currency?.iso3}
              </p>
            </div>
          </div>
        )
      }

      // Title + conditional display icon
      const Icon = event.eventReason.value === VALIDATED_OFFER_ID ? Eye : Edit
      const titleButtonOnClickHandler = () => {
        if (event.eventReason.value >= EVENT_ID_RANGES.events.start) {
          toggleModal("edit", event)
        } else {
          window.open(`/Configurator/Orders/Details/${event.configOrderId}`, "_blank")
        }
      }
      const titleHtml = (
        <div className="d-flex align-items-center">
          {event.eventReason.label}
          {event.eventReason.value <= EVENT_ID_RANGES.complaint.end &&
            event.eventReason.value >= EVENT_ID_RANGES.complaint.start && (
              <ValidateConfirmation
                onTouch={createEventComplaintSuccess}
                onSuccess={getPersonHistoryHandler}
              />
              // <Button size="sm" color="flat-secondary" onClick={createEventComplaintSuccess}>
              //   <CheckCircle color="green" size={15} onSuccess={getPersonHistoryHandler} />
              // </Button>
            )}
          {event.configOrderId && event.eventReason.value > EVENT_ID_RANGES.system.end && (
            <Button size="sm" color="flat-secondary" onClick={titleButtonOnClickHandler}>
              <Icon size={15} />
            </Button>
          )}
          {isAdminCRM && event.eventReason.value > EVENT_ID_RANGES.system.end && (
            <DeleteConfirmation
              id={event.configOrderId ? event.configOrderId : event.id}
              endpoint={event.configOrderId ? "OrdersAPI" : "Events"}
              onSuccess={getPersonHistoryHandler}
            />
          )}
        </div>
      )

      // Concat all data
      const customContent = (
        <div className="d-flex flex-wrap flex-sm-row flex-column">
          {amountHtml}
          {showroomHtml}
          {collaboratorHtml}
          {partnerHtml}
        </div>
      )
      formatedData.push({
        title: titleHtml,
        content: event.description,
        meta: dateISO(event.on, true, true),
        color: eventColorSelection(event.eventReason.value),
        customContent
      })
    }
    setData(formatedData)
  }

  useEffect(() => {
    getPersonHistoryHandler()
  }, [])

  // ** Local html snippets
  const BypassMissingData = ({ selectedEventType }) => {
    const [askedClientData, setAskedClientData] = useState(false)
    const onChangeHandler = (e) => {
      setAskedClientData(e.target.checked)
    }
    return (
      <div className="d-flex align-items-center justify-content-between w-100">
        <div className="form-check form-check-danger">
          <Input
            type="checkbox"
            name="askedCustomerForInfo"
            id="askedCustomerForInfo"
            onChange={onChangeHandler}
          />
          <Label className="form-check-label" for="askedCustomerForInfo">
            <b>I have asked the customer</b> and they refuse to provide the missing data.
          </Label>
        </div>
        <div>
          <Button
            color="flat-primary"
            disabled={!askedClientData}
            onClick={() => {
              toggleModal(selectedEventType, null, true)
            }}
          >
            Continue
          </Button>
        </div>
      </div>
    )
  }

  console.log("data", data)

  return (
    <Card>
      <CardAction
        title="Timeline"
        actions="reload"
        endReload={(endLoading) => getPersonHistoryHandler().then(() => endLoading())}
      >
        {editMode && (
          <CardHeader>
            <UncontrolledButtonDropdown>
              <DropdownToggle color="info" caret>
                Add new event
              </DropdownToggle>
              <DropdownMenu>
                <DropdownItem tag="a" onClick={() => toggleModal("offer")}>
                  <FileText size={14} />
                  <span className="align-middle ms-50">Offer</span>
                </DropdownItem>
                <DropdownItem tag="a" onClick={() => toggleModal("sale")}>
                  <ShoppingCart size={14} />
                  <span className="align-middle ms-50">Sale</span>
                </DropdownItem>
                <DropdownItem tag="a" onClick={() => toggleModal("complaint")}>
                  <Flag size={14} />
                  <span className="align-middle ms-50">Complaint</span>
                </DropdownItem>
                <DropdownItem tag="a" onClick={() => toggleModal("others")}>
                  <MoreHorizontal size={14} />
                  <span className="align-middle ms-50">Others (Visit, Call, etc.)</span>
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledButtonDropdown>
          </CardHeader>
        )}
        <div className="divider divider-primary divider-dotted">
          <div className="divider-text">Events</div>
        </div>
        <CardBody>
          {!data && <Spinner />}
          {data?.length > 0 && <Timeline data={data} />}
          {data?.length === 0 && <p className="text-muted">Empty timeline</p>}
        </CardBody>
      </CardAction>

      {/* Modals */}
      {/* TODO : better way ? */}
      {editMode && (
        <>
          <RenderModal id="offer" toggleModal={toggleModal} activeModal={activeModal}>
            <NewOfferSaleForm
              eventReason={{ id: 10, label: "Offer" }}
              personId={personStore.selectedPerson.id}
            />
          </RenderModal>
          <RenderModal id="sale" toggleModal={toggleModal} activeModal={activeModal}>
            <NewOfferSaleForm
              eventReason={{ id: 20, label: "Sale" }}
              personId={personStore.selectedPerson.id}
            />
          </RenderModal>
          <RenderModal id="complaint" toggleModal={toggleModal} activeModal={activeModal}>
            <ComplaitForm
              eventReason={{ id: 20, label: "Complaint" }}
              personId={personStore.selectedPerson.id}
            />
          </RenderModal>
          <RenderModal id="others" toggleModal={toggleModal} activeModal={activeModal}>
            <EventForm
              onSubmit={getPersonHistoryHandler}
              personId={personStore.selectedPerson.id}
            />
          </RenderModal>
          <RenderModal
            id="edit"
            footer={<span className="text-muted">event id: {eventToEdit?.id}</span>}
            toggleModal={toggleModal}
            activeModal={activeModal}
          >
            <EventForm
              onSubmit={getPersonHistoryHandler}
              personId={personStore.selectedPerson.id}
              eventToEdit={eventToEdit}
            />
          </RenderModal>
          <RenderModal
            id="missingData"
            toggleModal={toggleModal}
            activeModal={activeModal}
            footer={
              personStore.selectedPerson.addresses.length > 0 && (
                <BypassMissingData selectedEventType={selectedEventType} />
              )
            }
          >
            <Alert color="danger">
              <h4 className="alert-heading">
                <AlertCircle size={15} /> To create a new offer or sale, please provide the
                following data
              </h4>
              <div className="alert-body">
                <ul>
                  {personStore.selectedPerson.emails.length === 0 && <li>1 Email</li>}
                  {personStore.selectedPerson.phones.length === 0 && <li>1 Phone number</li>}
                  {personStore.selectedPerson.addresses.length === 0 && <li>1 Address</li>}
                </ul>
              </div>
            </Alert>
          </RenderModal>
        </>
      )}
    </Card>
  )
}

export default BasicTimeline
